import './CursorTooltip.scss';
import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { Placement } from '@Components/designConstants';

const TOOLTIP_SHOW_DELAY_MS = 150;

// Tracks hover/focus on a row and exposes a Popper virtual element anchored to the row's
// top/bottom edges at the cursor's x, so the tooltip slides horizontally with the mouse
// without covering the row. Keyboard focus (no cursor) anchors at the row's center.
// Shared by the Channel Performance bar view and the drawer's Inter-Channel Effects chart.
export const useCursorTooltip = () => {
  const rowRef = useRef<HTMLDivElement>(null);
  const hoveringRef = useRef(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [show, setShow] = useState(false);
  const [cursorX, setCursorX] = useState<number | null>(null);

  useEffect(() => () => clearTimeout(showTimerRef.current), []);

  // A new instance per cursor position triggers Popper to recompute placement.
  const target = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const rect = rowRef.current?.getBoundingClientRect() ?? new DOMRect();
        const x = cursorX ?? rect.left + rect.width / 2;
        return DOMRect.fromRect({ x, y: rect.top, width: 0, height: rect.height });
      }
    }),
    [cursorX]
  );

  const handlers = {
    onMouseEnter: (e: MouseEvent<HTMLDivElement>) => {
      hoveringRef.current = true;
      setCursorX(e.clientX);
      clearTimeout(showTimerRef.current);
      showTimerRef.current = setTimeout(() => setShow(true), TOOLTIP_SHOW_DELAY_MS);
    },
    onMouseMove: (e: MouseEvent<HTMLDivElement>) => setCursorX(e.clientX),
    onMouseLeave: () => {
      hoveringRef.current = false;
      clearTimeout(showTimerRef.current);
      setShow(false);
    },
    onFocus: () => {
      // Clicking a row also focuses it — keep the tooltip at the cursor in that case.
      if (hoveringRef.current) return;
      setCursorX(null);
      setShow(true);
    },
    onBlur: () => {
      if (hoveringRef.current) return;
      setShow(false);
    }
  };

  return { rowRef, show, target, handlers };
};

interface CursorTooltipPopoverProps {
  id: string;
  show: boolean;
  target: ReturnType<typeof useCursorTooltip>['target'];
  children: ReactNode;
}

export const CursorTooltipPopover = ({ id, show, target, children }: CursorTooltipPopoverProps) => (
  <Overlay show={show} target={target} placement={Placement.TOP} flip>
    <Popover id={id} className='dtc-mmm-cursor-tooltip-popover'>
      <Popover.Body>{children}</Popover.Body>
    </Popover>
  </Overlay>
);
